import React, { useRef, useState, useEffect } from "react"
import ReactDOM from "react-dom"
import { Spin } from "antd"
import { FleetDashboardMapProps } from "./types"
import { useFleetTracking } from "./useFleetTracking"
import VehiclePanel from "./components/VehiclePanel"
import MapLegend from "./components/MapLegend"
import MapControls from "./components/MapControls"
import FleetStatsBar from "./components/FleetStatsBar"
import {
	containerStyle,
	loadingOverlayStyle,
	errorContainerStyle,
} from "./styles"

const PANEL_WIDTH = 340

const FleetDashboardMap: React.FC<FleetDashboardMapProps> = ({
	businessId,
	mapContainerStyle: customMapStyle,
	onVehicleClick,
	onFleetUpdate,
}) => {
	const mapWrapperRef = useRef<HTMLDivElement>(null)
	const [showLegend, setShowLegend] = useState(false)
	const [fullscreenElement, setFullscreenElement] = useState<Element | null>(
		null
	)

	const {
		mapContainerRef,
		isLoading,
		error,
		vehicles,
		selectedVehicle,
		vehicleTasks,
		showPOIs,
		handleTogglePOIs,
		handleCloseVehiclePanel,
	} = useFleetTracking({ businessId, onVehicleClick, onFleetUpdate })

	useEffect(() => {
		const handleFullscreenChange = () => {
			setFullscreenElement(document.fullscreenElement)
		}
		document.addEventListener("fullscreenchange", handleFullscreenChange)
		return () =>
			document.removeEventListener("fullscreenchange", handleFullscreenChange)
	}, [])

	if (error) {
		return (
			<div style={{ ...containerStyle, ...customMapStyle }}>
				<div style={errorContainerStyle}>{error}</div>
			</div>
		)
	}

	const mapOverlays = (
		<>
			{fullscreenElement && selectedVehicle && (
				<VehiclePanel
					vehicle={selectedVehicle}
					tasks={vehicleTasks}
					onClose={handleCloseVehiclePanel}
					isFullscreen
				/>
			)}
			<MapControls
				showPOIs={showPOIs}
				showLegend={showLegend}
				onTogglePOIs={handleTogglePOIs}
				onToggleLegend={() => setShowLegend((v) => !v)}
			/>
			{showLegend && <MapLegend />}
		</>
	)

	return (
		<div style={{ ...containerStyle, ...customMapStyle }}>
			<FleetStatsBar
				vehicles={selectedVehicle ? [selectedVehicle] : vehicles}
			/>

			<div style={{ display: "flex", flex: 1, minHeight: 0 }}>
				{/* Map */}
				<div
					ref={mapWrapperRef}
					style={{ flex: 1, position: "relative", minWidth: 0 }}
				>
					{isLoading && (
						<div style={loadingOverlayStyle}>
							<Spin size="large" />
						</div>
					)}
					<div
						ref={mapContainerRef}
						style={{ width: "100%", height: "100%", position: "relative" }}
					/>
					{fullscreenElement
						? ReactDOM.createPortal(mapOverlays, fullscreenElement)
						: mapOverlays}
				</div>

				{/* Side panel */}
				<div
					style={{
						width: selectedVehicle ? PANEL_WIDTH : 0,
						flexShrink: 0,
						overflow: "hidden",
						transition: "width 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
						borderLeft: selectedVehicle ? "1px solid #e8e8e8" : "none",
					}}
				>
					{selectedVehicle && (
						<VehiclePanel
							vehicle={selectedVehicle}
							tasks={vehicleTasks}
							onClose={handleCloseVehiclePanel}
						/>
					)}
				</div>
			</div>
		</div>
	)
}

export default FleetDashboardMap
